import React, { FC, useEffect, useMemo, useState } from 'react';
import { Breadcrumb, Menu } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { Spin } from 'common/components';
import { ERoute } from 'common/const';
import { stringToNumber } from 'common/helpers';
import { DEFAULT_VALUE_1 } from 'common/config';
import { ArrowRightShort } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import {
  SettingsSubdivisionAddressListTab,
  SettingsSubdivisionGeneralTab,
  SettingsSubdivisionName,
  SettingsSubdivisionUserListTab,
} from 'entities/Settings/components';
import { ESettingsMenuTab, ESettingsTab } from 'entities/Settings/Settings.enum';
import { getSubdivisionSettingsBreadcrumbItems, getSubdivisionTreeKeysBySubdivisionId } from 'entities/Settings/Settings.helper';
import {
  getSubdivisionById,
  selectIsSubdivisionLoading,
  selectSubdivision,
  selectSubdivisionList,
  updateSubdivision,
} from 'entities/Subdivision/Subdivision.slice';
import { IUpdateSubdivisionPayload } from 'entities/Subdivision/Subdivision.models';

export const SettingsSubdivision: FC = () => {
  const dispatch = useAppDispatch();
  // subdivision
  const subdivision = useAppSelector(selectSubdivision);
  const subdivisionList = useAppSelector(selectSubdivisionList);
  const isSubdivisionLoading = useAppSelector(selectIsSubdivisionLoading);

  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const { id, itemMenu } = useParams();
  const navigate = useNavigate();

  const selectedSubdivisionTreeKeys = getSubdivisionTreeKeysBySubdivisionId(subdivisionList, id);
  const subdivisionBreadcrumbItems = getSubdivisionSettingsBreadcrumbItems(subdivisionList, selectedSubdivisionTreeKeys, (id) => {
    navigate(`${ERoute.Settings}/${ESettingsTab.Subdivision}/${id}/${itemMenu}`);
  });

  const handleUpdateSubdivision = async (payload: IUpdateSubdivisionPayload): Promise<void> => {
    dispatch(updateSubdivision(payload));
  };

  const header = useMemo(() => {
    return (
      <>
        <div className="subdivision-settings__breadcrumb mb-6">
          <Breadcrumb
            items={subdivisionBreadcrumbItems.length > DEFAULT_VALUE_1 ? subdivisionBreadcrumbItems : []}
            separator={<ArrowRightShort className="icon-arrow-right-short-light-grey" />}
          />
        </div>

        <SettingsSubdivisionName
          subdivision={subdivision}
          loading={isSubdivisionLoading}
          onSubdivisionUpdate={handleUpdateSubdivision}
        />

        <Menu
          items={[
            { key: ESettingsMenuTab.General, label: 'Настройки подразделения' },
            { key: ESettingsMenuTab.UserList, label: 'Пользователи' },
            { key: ESettingsMenuTab.AddressList, label: 'Адреса доставки' },
          ]}
          selectedKeys={[!itemMenu ? ESettingsMenuTab.General : itemMenu]}
          onClick={({ key }) => navigate(`${ERoute.Settings}/${ESettingsTab.Subdivision}/${subdivision?.id}/${key}`)}
          mode="horizontal"
        />
      </>
    );
  }, [subdivision, isSubdivisionLoading, itemMenu]);

  const renderContent = () => {
    switch (itemMenu) {
      case ESettingsMenuTab.AddressList: {
        return <SettingsSubdivisionAddressListTab header={header} subdivision={subdivision} />;
      }
      case ESettingsMenuTab.UserList: {
        return <SettingsSubdivisionUserListTab header={header} subdivision={subdivision} />;
      }
      default: {
        return (
          <SettingsSubdivisionGeneralTab
            header={header}
            subdivision={subdivision}
            onSubdivisionUpdate={handleUpdateSubdivision}
          />
        );
      }
    }
  };

  useEffect(() => {
    const fetch = async () => {
      if (id) {
        await dispatch(getSubdivisionById({ id: stringToNumber(id) }));
      }

      setFetchLoading(false);
    };

    fetch();
  }, [id]);

  if (!id) {
    return null;
  }

  if (fetchLoading) {
    return (
      <div className="h-full flex align-center justify-center flex-1">
        <Spin />
      </div>
    );
  }

  return <div className="relative h-full flex direction-column subdivision-settings">{renderContent()}</div>;
};
